import React from "react";
import { BrandButton as Button } from "@/components";

// Operator preset definitions
const OPERATOR_SETS = {
  arithmetic: [
    { value: '+', label: '+', variant: 'outline-success' },
    { value: '-', label: '−', variant: 'outline-danger' },
    { value: '*', label: '×', variant: 'outline-warning' },
  ],
  arithmeticWithEquals: [
    { value: '+', label: '+', variant: 'outline-success' },
    { value: '-', label: '−', variant: 'outline-danger' },
    { value: '*', label: '×', variant: 'outline-warning' },
    { value: '=', label: '=', variant: 'outline-dark' },
  ],
  arithmeticWithComparison: [
    { value: '+', label: '+', variant: 'outline-success' },
    { value: '-', label: '−', variant: 'outline-danger' },
    { value: '*', label: '×', variant: 'outline-warning' },
    { value: '=', label: '=', variant: 'outline-dark' },
    { value: '<', label: '<', variant: 'outline-info' },
    { value: '>', label: '>', variant: 'outline-info' },
    { value: '<=', label: '≤', variant: 'outline-info' },
    { value: '>=', label: '≥', variant: 'outline-info' },
  ],
};

/**
 * Reusable inline formula builder - styled to match the column formula builder.
 * Renders the open/active builder only. The parent controls the toggle visibility.
 *
 * Props:
 *   getDisplayText     fn()                  → string shown in the preview box
 *   elements           array                 items available to add (columns or sources)
 *   getElementLabel    fn(element, index)    → button label string
 *   elementSectionLabel string               heading above element buttons (default "Add to Formula:")
 *   onAddElement       fn(element, index)    called when an element button is clicked
 *   operators          string | array        operator set name ("arithmetic", "arithmeticWithEquals", "arithmeticWithComparison") or custom array
 *   onAddOperator      fn(value)             called when an operator button is clicked
 *   onClear            fn()                  clears the formula
 *   onCancel           fn()                  cancels and closes the builder
 *   onConfirm          fn()                  confirms/saves the formula
 *   confirmLabel       string               label for the confirm button (default "✓ Confirm Formula")
 */
export default function FormulaBuilder({
  getDisplayText,
  elements = [],
  getElementLabel = (_, index) => `Item ${index + 1}`,
  elementSectionLabel = "Add to Formula:",
  onAddElement,
  operators = "arithmetic",
  onAddOperator,
  onClear,
  onCancel,
  onConfirm,
  confirmLabel = "✓ Confirm Formula",
}) {
  // Resolve operators - accept either a preset name (string) or custom array
  const operatorButtons = typeof operators === 'string' 
    ? (OPERATOR_SETS[operators] || OPERATOR_SETS.arithmetic)
    : operators;
  return (
    <div>
      {/* Formula preview */}
      <div
        className="mb-3 p-3"
        style={{
          backgroundColor: "#f8f9fa",
          borderRadius: "4px",
          border: "2px dashed #17a2b8",
          minHeight: "50px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "var(--font-lg)",
          fontFamily: "monospace",
        }}
      >
        {typeof getDisplayText === "function" ? getDisplayText() : "Build your formula below..."}
      </div>

      {/* Element buttons */}
      <div className="mb-3">
        <label className="form-label ">{elementSectionLabel}</label>
        <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
          {elements.map((el, index) => (
            <Button
              key={index}
              onClick={() => onAddElement?.(el, index)}
              variant="outline-primary"
              size="sm"
            >
              {getElementLabel(el, index)}
            </Button>
          ))}
        </div>
      </div>

      {/* Operator buttons */}
      <div className="mb-3">
        <label className="form-label ">Add Operator:</label>
        <div style={{ display: "flex", gap: "0.5rem" }}>
          {operatorButtons.map((op) => (
            <Button
              key={op.value}
              onClick={() => onAddOperator?.(op.value)}
              variant={op.variant || "outline-secondary"}
              style={{ minWidth: "60px", padding: "0.5rem 1rem" }}
            >
              <span style={{ fontSize: "2rem", lineHeight: "1", fontFamily: "Arial, sans-serif" }}>{op.label || op.value}</span>
            </Button>
          ))}
        </div>
      </div>

      {/* Action buttons */}
      <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
        <Button onClick={onClear} variant="outline-secondary" size="sm">
          Clear
        </Button>
        {onCancel && (
          <Button onClick={onCancel} variant="outline-secondary" size="sm">
            Cancel
          </Button>
        )}
        <div style={{ marginLeft: "auto" }}>
          <Button onClick={onConfirm} variant="success" size="sm" style={{ fontWeight: "bold" }}>
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
